import { hasValue } from "@cak-legacy/core-utils";
import {
  ArrayFieldSchema,
  FieldSchema,
  isArrayFieldSchema,
  isCheckboxGroupFieldSchema,
  isFileFieldSchema,
  isObjectFieldSchema,
  ObjectFieldSchema,
  Schema
} from "@cak-legacy/generation-core";
import { getNumberOfItemsInArray, getValueFromObject, getDependsonResult } from "@cak-legacy/generation-overview-core";
import { TFunction } from "@cak-legacy/i18n-iprox";
import { FormulierSectie } from "@cak-legacy/klantondersteuning-client";

import { PdfRequestBuilder } from "../builders/pdfRequestBuilder";
import { SectieBuilder } from "../builders/sectieBuilder";

export class PdfJsonConverter {
  private readonly builder = new PdfRequestBuilder();

  constructor(
    private readonly schema: Schema,
    private readonly values: any,
    private readonly overviewValues: any,
    private readonly t: TFunction
  ) {}

  getJson(): FormulierSectie[] {
    Object.entries(this.schema).forEach((sectie) => {
      this.renderSection(sectie[0], sectie[1]);
    });

    const secties = this.builder.build();

    return secties
      .filter((sectie) => sectie.sectie.vragen.length > 0)
      .map(
        (sectie) =>
          new FormulierSectie({
            titel: sectie.sectie.titel,
            vragen: sectie.sectie.vragen
          })
      );
  }

  private renderSection(name: string, field: FieldSchema, overrideTitel?: string | undefined): void {
    if (!("type" in field)) {
      return;
    }

    const result = getDependsonResult(name, this.values, field.dependsOn);
    if (result.hide === true) {
      return;
    }
    if (isObjectFieldSchema(field)) {
      const sectieBuilder = this.builder.addSectie(overrideTitel ?? field.label ?? "");

      this.renderObjectField(name, field, sectieBuilder);
    }

    if (isArrayFieldSchema(field)) {
      this.renderArrayField(name, field);
    }
  }

  private renderObjectField(name: string, field: ObjectFieldSchema, sectieBuilder: SectieBuilder): void {
    let currentSectieBuilder = sectieBuilder;
    let isCreateSection = false;

    Object.entries(field.properties).forEach((property) => {
      const propertyName = `${name}.${property[0]}`;
      const propertyObject = property[1];

      const result = getDependsonResult(name, this.values, propertyObject.dependsOn);
      if (result.hide === true) {
        return;
      }
      if (isArrayFieldSchema(propertyObject)) {
        isCreateSection = true;
        this.renderArrayField(propertyName, propertyObject, field.label);
      } else if (isObjectFieldSchema(propertyObject)) {
        isCreateSection = true;

        const propertyLabel = hasValue(propertyObject.label) ? propertyObject.label.trim() : undefined;

        const overrideTitle = hasValue(propertyLabel)
          ? `${field.label} - ${propertyLabel}`
          : `${field.label}`;

        this.renderSection(propertyName, propertyObject, overrideTitle);
      } else {
        if (isCreateSection) {
          const sectionTitle = `${field.label} - ${this.t("sections.pdf.vervolg.title")}`;
          currentSectieBuilder = this.builder.addSectie(sectionTitle);
          isCreateSection = false;
        }

        if (property[1].label) {
          currentSectieBuilder.addVraag(
            property[1].label,
            this.getFieldValue(propertyObject, propertyName) ?? "-"
          );
        }
      }
    });
  }

  private readonly getFieldValue = (property: FieldSchema, propertyName: string): string | string[] => {
    const value = getValueFromObject(this.overviewValues, propertyName) as any;

    if (isFileFieldSchema(property) && value) {
      return Array.from(value).map((file: File) => file.name);
    }

    if (isCheckboxGroupFieldSchema(property) && Array.isArray(value)) {
      return property.options.filter((option) => value.includes(option.value)).map((option) => option.label);
    }

    return value;
  };

  private renderArrayField(name: string, field: ArrayFieldSchema, titelPrefix?: string): void {
    const items = Array.from(new Array(getNumberOfItemsInArray(this.values, name)), (_x, i) => i);

    for (let i = 0; i < items.length; i++) {
      const itemNumber = items.length > 1 ? `(${i + 1})` : "";
      const fieldLabel = hasValue(field.label) ? field.label.trim() : "";

      const title = hasValue(titelPrefix)
        ? `${titelPrefix} - ${fieldLabel} ${itemNumber}`
        : `${fieldLabel} ${itemNumber}`;

      this.renderSection(`${name}[${i}]`, field.items, title.trim());
    }
  }
}
